import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { loginUser } from "../utils/api";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // ✅ eye toggle
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter email and password."); return; }
    setLoading(true); setError("");
    try {
      const res = await loginUser({ email, password });
      const actualRole = res.data.user.role;

      if (actualRole !== selectedRole) {
        setError(actualRole === "barber"
          ? "Please use Customer Login."
          : "Please use Salon/Spa Login.");
        setLoading(false); return;
      }

      login(res.data.token, res.data.user);
      navigate(actualRole === "barber" ? "/dashboard" : "/shops");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password.");
    } finally { setLoading(false); }
  };

  if (!selectedRole) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logoWrap}>
            <span style={styles.logoIcon}>💈</span>
            <span style={styles.logoText}>TrimSpot</span>
          </div>
          <h1 style={styles.title}>Welcome Back!</h1>
          <p style={styles.subtitle}>Who are you logging in as?</p>

          <div style={styles.roleGrid}>
            <div style={styles.roleCard}>
              <p style={styles.roleIcon}>👤</p>
              <p style={styles.roleTitle}>Customer</p>
              <p style={styles.roleDesc}>Book appointments</p>
              <div onClick={() => setSelectedRole("customer")} style={styles.roleBtn}>Login →</div>
            </div>
            <div style={{ ...styles.roleCard, ...styles.roleCardSalon }}>
              <p style={styles.roleIcon}>💼</p>
              <p style={styles.roleTitle}>Salon / Spa</p>
              <p style={styles.roleDesc}>Manage your business</p>
              <div onClick={() => setSelectedRole("barber")} style={{ ...styles.roleBtn, background: "#333" }}>Login →</div>
            </div>
          </div>

          <p style={styles.registerText}>
            New to TrimSpot?{" "}
            <Link to="/register" style={styles.link}>Create free account →</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <button onClick={() => { setSelectedRole(null); setError(""); }} style={styles.backBtn}>← Back</button>

        <div style={{
          ...styles.badge,
          background: selectedRole === "barber" ? "#1a1a2e" : "rgba(233,69,96,0.15)",
          border: `1px solid ${selectedRole === "barber" ? "#444" : "var(--accent)"}`
        }}>
          {selectedRole === "customer" ? "👤 Customer Login" : "💼 Salon / Spa Login"}
        </div>

        <h2 style={styles.title}>Welcome Back!</h2>
        <p style={styles.subtitle}>Enter your credentials to continue</p>

        {error && <div style={styles.error}>{error}</div>}

        <label style={styles.label}>Email Address</label>
        <input
          type="text"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
        />

        <label style={styles.label}>Password</label>
        {/* ✅ Password field with eye toggle */}
        <div style={styles.passwordWrap}>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.passwordInput}
            autoComplete="new-password"
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={styles.eyeBtn}
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>

        <button onClick={handleLogin}
          style={{ ...styles.loginBtn, background: "var(--accent)" }}
          disabled={loading}>
          {loading ? "Logging in..." : `Login as ${selectedRole === "barber" ? "Salon Owner 💼" : "Customer 👤"}`}
        </button>

        <div style={styles.switchRole}>
          <span style={styles.switchText}>
            {selectedRole === "customer" ? "Are you a Salon Owner? " : "Are you a Customer? "}
          </span>
          <button onClick={() => {
            setSelectedRole(selectedRole === "customer" ? "barber" : "customer");
            setError("");
          }} style={styles.switchBtn}>
            Switch →
          </button>
        </div>

        <p style={styles.registerText}>
          Don't have an account?{" "}
          <Link to="/register" style={styles.link}>Register free →</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" },
  card: { background: "var(--bg-card)", borderRadius: "20px", padding: "36px 32px", width: "100%", maxWidth: "460px", border: "1px solid var(--border)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" },
  logoWrap: { display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "16px" },
  logoIcon: { fontSize: "36px" },
  logoText: { fontSize: "1.6rem", fontWeight: "900", color: "white" },
  title: { textAlign: "center", fontSize: "1.6rem", color: "white", marginBottom: "6px" },
  subtitle: { textAlign: "center", color: "var(--text-muted)", marginBottom: "28px", fontSize: "14px" },
  roleGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "24px" },
  roleCard: { background: "var(--bg-secondary)", border: "1px solid var(--accent)", borderRadius: "14px", padding: "20px 14px", textAlign: "center", transition: "all 0.2s" },  roleCardSalon: { border: "1px solid var(--border)" },
  roleIcon: { fontSize: "32px", margin: "0 0 8px" },
  roleTitle: { fontWeight: "bold", fontSize: "15px", color: "white", margin: "0 0 4px" },
  roleDesc: { color: "var(--text-muted)", fontSize: "12px", margin: "0 0 12px" },
  roleBtn: { background: "var(--accent)", color: "white", padding: "7px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "bold" },
  backBtn: { background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "14px", marginBottom: "16px", padding: 0 },
  badge: { color: "var(--accent)", padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold", display: "inline-block", marginBottom: "16px" },
  error: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", fontSize: "14px", border: "1px solid rgba(233,69,96,0.3)" },
  label: { display: "block", color: "var(--text-secondary)", fontSize: "13px", fontWeight: "500", marginBottom: "6px" },
  input: { width: "100%", padding: "13px 14px", borderRadius: "10px", fontSize: "15px", marginBottom: "16px", boxSizing: "border-box" },
  // ✅ Password wrap with eye button
  passwordWrap: { position: "relative", marginBottom: "16px" },
  passwordInput: { width: "100%", padding: "13px 48px 13px 14px", borderRadius: "10px", fontSize: "15px", boxSizing: "border-box", background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" },
  eyeBtn: { position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "18px", padding: "4px", lineHeight: 1 },
  loginBtn: { width: "100%", padding: "14px", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "16px", fontWeight: "bold", marginBottom: "16px" },
  switchRole: { textAlign: "center", marginBottom: "16px", padding: "12px", background: "var(--bg-secondary)", borderRadius: "8px", border: "1px solid var(--border)" },
  switchText: { color: "var(--text-muted)", fontSize: "13px" },
  switchBtn: { background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: "13px", fontWeight: "bold" },
  registerText: { textAlign: "center", color: "var(--text-muted)", fontSize: "14px", margin: 0 },
  link: { color: "var(--accent)", fontWeight: "bold", textDecoration: "none" },
};